import React, { useEffect } from 'react';
import { useParams, Navigate, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import apiClient from '../../api/axios';
import { sanitizeHtml } from '../../utils/sanitize';

import { PageHero } from '../../components/common/PageHero';

export const ProjectDetail: React.FC = () => {
    const { slug } = useParams<{ slug: string }>();
    const navigate = useNavigate();
    const { t } = useTranslation();

    const { data: project, isLoading, isError } = useQuery({
        queryKey: ['publicProject', slug],
        queryFn: async () => {
            const response = await apiClient.get(`/public/projects/${slug}`);
            return response.data.data;
        },
        enabled: !!slug,
        retry: false
    });

    useEffect(() => {
        if (project?.title) {
            document.title = `${project.title} | Mosaik Offenbach`;
        }
    }, [project?.title]);

    if (isLoading) {
        return (
            <div className="flex justify-center items-center py-32 min-h-[60vh]">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
            </div>
        );
    }

    if (isError || !project) {
        return <Navigate to="/projekte" replace />;
    }

    return (
        <div className="flex flex-col bg-background min-h-screen pb-16">
            <PageHero
                title={project.title}
                imageUrl={project.imageUrl}
                breadcrumb={[
                    { label: t('common.home'), to: '/' },
                    { label: t('projects.breadcrumb'), to: '/projekte' },
                    { label: project.title }
                ]}
            />

            <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 w-full -mt-8 relative z-10">

                <div className="bg-white rounded-3xl shadow-sm border border-border p-8 sm:p-12 text-lg leading-relaxed text-foreground/80">
                    <button
                        onClick={() => navigate('/projekte')}
                        className="inline-flex items-center gap-2 text-sm font-semibold text-primary hover:text-primary/80 transition-colors mb-8"
                    >
                        <ArrowLeft className="w-4 h-4" />
                        {t('events.back_to_overview')}
                    </button>

                    <div
                        className="prose prose-lg prose-primary max-w-none text-foreground/80 leading-relaxed"
                        dangerouslySetInnerHTML={{ __html: sanitizeHtml(project.description) }}
                    />
                </div>
            </div>
        </div>
    );
};
